/*
 *   File : report.js
 *   Author URI : https://evoqins.com
 *   Description : Obligation reconciliation report (BSE payin obligation)
 *   Integrations :
 *   Version : v1.1
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Modal } from "bootstrap";

import { NavigationHeader } from "../../Components/Header";
import { CustomSearchSelectBox, DataTableContainer, DatePicker, FilePreview, FileUpload, SearchInput } from "../../Components/FormElements";
import { DownloadButton, GradientButton, OutlineButton } from "../../Components/Cta";
import { CustomLoader, EmptyScreen } from "../../Components/Other";
import { Icon } from "../../Components/Icon";

import Colors from "../../Styles/color.module.scss";

const PAGE_NAVIGATION = ["Dashboard", "Obligation Reports"];

const DEPOSITORY_OPTIONS = [
    { label: "All Depositories", value: "" },
    { label: "NSDL", value: "NSDL" },
    { label: "CDSL", value: "CDSL" },
];

// const STATUS_OPTIONS = [
//     { label: "All Statuses", value: "" },
//     { label: "MATCHED", value: "MATCHED" },
//     { label: "UNMATCHED", value: "UNMATCHED" },
// ];

const TABLE_STYLE = {
    table: {
        style: {
            padding: "0px",
            borderRadius: "16px",
            background: Colors.white,
            border: `1px solid ${Colors.bright_gray}`,
        },
    },
    tableBody: {
        style: {
            margin: "0px",
            marginTop: "0px !important"
        }
    },
    headRow: {
        style: {
            background: Colors.alice_blue,
            borderRadius: "16px 16px 0px 0px",
            border: "0",
            padding: "16px 32px",
            color: Colors.black,
            fontFamily: "Poppins-Regular",
            fontSize: "16px",
            lineHeight: "24px",
        }
    },
    rows: {
        style: {
            margin: "0",
            background: "transparent",
            border: "0 !important",
            borderTop: `1px solid ${Colors.bright_gray} !important`,
            borderRadius: "0",
            color: Colors.black,
            fontFamily: "Poppins-Regular",
            fontSize: "16px",
            lineHeight: "24px",
            padding: "16px 24px",
        },
    },
    cells: {
        style: {
            paddingLeft: "0px",
            paddingRight: "12px",
        },
    },
    headCells: {
        style: {
            paddingLeft: "0px",
            paddingRight: "12px",
        },
    },
    pagination: {
        style: {
            borderTop: "none",
            color: Colors.outer_space,
            fontSize: "16px",
            fontFamily: "Poppins-Regular",
        },
    }
};

function ObligationReport() {
    const [apiLoader, setApiLoader] = useState(true);
    const [btnLoader, setBtnLoader] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [downloadingFileName, setDownloadingFileName] = useState("");
    const [uploadModal, setUploadModal] = useState(false);
    const [records, setRecords] = useState([]);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLength, setTotalLength] = useState(0);
    const [paginationData, setPaginationData] = useState(null);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    // const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);
    const [selectedDepository, setSelectedDepository] = useState(DEPOSITORY_OPTIONS[0]);
    const [searchQuery, setSearchQuery] = useState("");
    console.log('searchQuery', searchQuery);


    const [appliedFilter, setAppliedFilter] = useState({
        fromDate: "",
        toDate: "",
        // status: "",
        depository: "",
        search: ""
    });

    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState("");

    // status is filtered client-side because the API spec accepts only
    // from_date, to_date, depository, search, page, limit. Filter applies to current page only.
    // const filteredRecords = useMemo(() => {
    //     if (!appliedFilter.status) {
    //         return records;
    //     }
    //     return records.filter(row => row.status === appliedFilter.status);
    // }, [records, appliedFilter.status]);

    const SUMMARY_COLUMNS = [
        {
            name: "Created",
            selector: row => row.created,
            width: "220px",
            cell: row => <span>{_formatTimestamp(row.created)}</span>,
        },
        // {
        //     name: "Status",
        //     selector: row => row.status,
        //     width: "140px",
        //     cell: row => <span>{row.status || "--"}</span>,
        // },
        {
            name: "Client code",
            selector: row => row.client_code,
            width: "140px",
            cell: row => <span>{row.client_code || "--"}</span>,
        },
        {
            name: "ISIN",
            selector: row => row.isin_code,
            width: "180px",
            cell: row => <span>{row.isin_code || "--"}</span>,
        },
        {
            name: "Quantity",
            selector: row => row.quantity,
            width: "130px",
            cell: row => <span>{row.quantity ?? "--"}</span>,
        },
        {
            name: "Depository",
            selector: row => row.depository_type,
            width: "130px",
            cell: row => <span>{row.depository_type || "--"}</span>,
        },
        {
            name: "BSE order no.",
            selector: row => row.bse_order_no,
            width: "170px",
            cell: row => <span>{row.bse_order_no || "--"}</span>,
        },
        {
            name: "BSE order date",
            selector: row => row.bse_order_date,
            width: "150px",
            cell: row => <span>{row.bse_order_date || "--"}</span>,
        },
        {
            name: "Settlement no.",
            selector: row => row.bse_settlement_number,
            width: "150px",
            cell: row => <span>{row.bse_settlement_number || "--"}</span>,
        },
        {
            name: "Settlement date",
            selector: row => row.bse_settlement_date,
            width: "150px",
            cell: row => <span>{row.bse_settlement_date || "--"}</span>,
        }
    ];

    useEffect(() => {
        _getReconciliationList(1, appliedFilter, pageSize, true);
    }, []);

    useEffect(() => {
        if (uploadModal === true) {
            const modalElement = document.getElementById("obligation-upload-modal");
            if (!modalElement) {
                return;
            }

            const modal = new Modal(modalElement, {
                backdrop: true,
                keyboard: false
            });

            const handleClose = () => _resetUploadModal();
            modalElement.addEventListener("hidden.bs.modal", handleClose);
            modal.show();

            return () => {
                modalElement.removeEventListener("hidden.bs.modal", handleClose);
            };
        }
    }, [uploadModal]);

    function _getBaseUrl() {
        return process.env.REACT_APP_API_URL?.endsWith("/")
            ? process.env.REACT_APP_API_URL
            : `${process.env.REACT_APP_API_URL}/`;
    }

    function _getAuthHeader() {
        return {
            Authorization: `Bearer ${localStorage.getItem("admin_session")}`
        };
    }

    function _parseDdMmYyyy(value) {
        if (!value) {
            return undefined;
        }
        const [day, month, year] = value.split("-");
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    function _toApiDate(value) {
        if (!value) {
            return "";
        }
        const [day, month, year] = value.split("-");
        return `${year}-${month}-${day}`;
    }

    function _formatTimestamp(iso) {
        if (!iso) {
            return "--";
        }
        const date = new Date(iso);
        if (isNaN(date.getTime())) {
            return "--";
        }
        const pad = n => String(n).padStart(2, "0");
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const hour24 = date.getHours();
        const ampm = hour24 >= 12 ? "PM" : "AM";
        const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
        return `${pad(date.getDate())} ${months[date.getMonth()]} ${date.getFullYear()}, ${pad(hour12)}:${pad(date.getMinutes())} ${ampm}`;
    }

    async function _getReconciliationList(page = pageNumber, filterSet = appliedFilter, limit = pageSize, showGlobalLoader = false) {
        if (showGlobalLoader) {
            setApiLoader(true);
        } else {
            setBtnLoader(true);
        }

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);

        if (filterSet.fromDate) {
            params.append("from_date", filterSet.fromDate);
        }
        if (filterSet.toDate) {
            params.append("to_date", filterSet.toDate);
        }
        if (filterSet.depository) {
            params.append("depository", filterSet.depository);
        }
        if (filterSet.search) {
            params.append("search", filterSet.search);
        }

        try {
            const response = await axios.get(
                `${_getBaseUrl()}depository/reconciliation-report?${params.toString()}`,
                {
                    headers: {
                        ..._getAuthHeader(),
                        Accept: "application/json",
                    },
                    timeout: 90000
                }
            );

            const responseData = response.data;
            if (responseData.status_code === 200) {
                setRecords(responseData.data.records || []);
                const apiPagination = responseData.data.pagination || { page: page, limit: limit, total: 0, total_pages: 1 };
                setPageNumber(apiPagination.page);
                setTotalPages(apiPagination.total_pages || 1);
                setTotalLength(apiPagination.total || 0);
                setPaginationData({
                    rowsPerPageText: `${apiPagination.page} | Total records: ${apiPagination.total || 0}`,
                });
            } else {
                toast.dismiss();
                toast.error(responseData.message || "Unable to fetch reconciliation report");
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || "Unable to fetch reconciliation report");
        }

        setApiLoader(false);
        setBtnLoader(false);
    }

    async function _downloadBatchFile() {
        try {
            const response = await axios.get(
                `${_getBaseUrl()}depository/batches/download`,
                {
                    headers: {
                        ..._getAuthHeader(),
                    },
                    timeout: 90000
                }
            );

            const responseData = response.data;

            if (responseData?.status_code === 200 && Array.isArray(responseData?.data?.files)) {
                const files = responseData.data.files;

                if (files.length === 0) {
                    toast.dismiss();
                    toast.error("No files available to download");
                    return false;
                }

                for (let i = 0; i < files.length; i++) {
                    const { url, file_name } = files[i];
                    if (!url) continue;

                    const link = document.createElement("a");
                    link.href = url;
                    link.download = file_name || "obligation-report";
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    if (i < files.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }

                toast.dismiss();
                toast.success(`${files.length} file(s) downloaded successfully`);
                return true;
            } else {
                toast.dismiss();
                toast.error(responseData?.message || "Unable to download batch file");
                return false;
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || "Unable to download batch file");
            return false;
        }
    }

    async function _handleDownloadAllBatches() {
        if (downloadingFileName) {
            return;
        }

        setDownloadingFileName("all");
        await _downloadBatchFile();
        setDownloadingFileName("");
    }

    function _handleFileChange(path, file) {
        if (!file) {
            return;
        }

        const fileName = (file.name || path || "").replace(/^.*[\\\/]/, "");
        const extension = fileName.split(".").pop()?.toLowerCase();
        if (extension !== "xlsx" && extension !== "xlsm") {
            toast.dismiss();
            toast.error("Please upload only .xlsx or .xlsm files");
            return;
        }

        setSelectedFile(file);
        setSelectedFileName(fileName);
    }

    function _resetUploadModal() {
        setUploadModal(false);
        setSelectedFile(null);
        setSelectedFileName("");
    }

    async function _uploadObligationFile() {
        if (!selectedFile) {
            toast.dismiss();
            toast.error("Please select a file to upload");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        setUploadLoader(true);

        try {
            const response = await axios.post(
                `${_getBaseUrl()}depository/upload-bse-obligation`,
                formData,
                {
                    headers: {
                        ..._getAuthHeader(),
                        "Content-Type": "multipart/form-data",
                    },
                    timeout: 90000
                }
            );

            const responseData = response.data;
            if (responseData?.status_code === 200) {
                toast.dismiss();
                toast.success("Obligation file uploaded successfully");
                const modalElement = document.getElementById("obligation-upload-modal");
                const modalInstance = Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                } else {
                    _resetUploadModal();
                }
                _getReconciliationList(1, appliedFilter, pageSize, true);
            } else {
                toast.dismiss();
                toast.error(responseData?.message || "Unable to upload obligation file");
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || "Unable to upload obligation file");
        }

        setUploadLoader(false);
    }

    function _handleApplyFilters() {
        const filterSet = {
            fromDate: _toApiDate(fromDate),
            toDate: _toApiDate(toDate),
            // status: selectedStatus?.value || "",
            depository: selectedDepository?.value || "",
            search: searchQuery.trim()
        };

        setAppliedFilter(filterSet);
        _getReconciliationList(1, filterSet, pageSize, false);
    }

    function _clearFilters() {
        setFromDate(null);
        setToDate(null);
        // setSelectedStatus(STATUS_OPTIONS[0]);
        setSelectedDepository(DEPOSITORY_OPTIONS[0]);
        setSearchQuery("");

        const defaultFilters = {
            fromDate: "",
            toDate: "",
            // status: "",
            depository: "",
            search: ""
        };
        setAppliedFilter(defaultFilters);
        _getReconciliationList(1, defaultFilters, pageSize, false);
    }

    function _handleSearch(value) {
        const trimmed = (value || "").trim();
        setSearchQuery(trimmed);
        const nextFilter = { ...appliedFilter, search: trimmed };
        setAppliedFilter(nextFilter);
        _getReconciliationList(1, nextFilter, pageSize, false);
    }

    function _handleSearchChange(value) {
        setSearchQuery(value);
        if (value === "") {
            const nextFilter = { ...appliedFilter, search: "" };
            setAppliedFilter(nextFilter);
            _getReconciliationList(1, nextFilter, pageSize, false);
        }
    }

    function _nextPage() {
        if (pageNumber >= totalPages) {
            return;
        }
        const nextPage = pageNumber + 1;
        _getReconciliationList(nextPage, appliedFilter, pageSize, true);
    }

    function _previousPage() {
        if (pageNumber <= 1) {
            return;
        }
        const previousPage = pageNumber - 1;
        _getReconciliationList(previousPage, appliedFilter, pageSize, true);
    }

    function _handleRowsPerPageChange(newSize) {
        setPageSize(newSize);
        setPageNumber(1);
        _getReconciliationList(1, appliedFilter, newSize, true);
    }

    return (
        <div className="h-100vh">
            <NavigationHeader
                type={2}
                data={PAGE_NAVIGATION}
                title="Obligation Reports"
            />

            <div className="ps-4 pe-lg-5 pe-md-2 pe-2 padding-42px-top h-100">
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div className="flex-grow-1" style={{ maxWidth: "480px", minWidth: "240px" }}>
                        <SearchInput
                            placeholder="Search with client code, ISIN, order no., settlement no."
                            value={searchQuery}
                            defaultValue={searchQuery}
                            valueChange={_handleSearchChange}
                            handleSearch={_handleSearch}
                            divClass="w-100"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <DownloadButton
                            className="h-fit-content"
                            disabled={downloadingFileName.length !== 0}
                            loader={downloadingFileName.length !== 0}
                            onPress={_handleDownloadAllBatches}
                        />
                        <GradientButton
                            label="Upload obligation"
                            className="h-fit-content"
                            disabled={uploadLoader}
                            onPress={() => setUploadModal(true)}
                        />
                    </div>
                </div>

                <div className="row mt-4 row-gap-4 align-items-center">
                    <div className="col-lg-3 col-md-6 col-12 position-relative">
                        <DatePicker
                            label="From date"
                            value={fromDate}
                            maxDate={toDate ? _parseDdMmYyyy(toDate) : new Date()}
                            dateFormat={"dd-MM-yyyy"}
                            displayFormat={"dd MMM yyyy"}
                            removeLabelOnFocus={true}
                            dob={(value) => setFromDate(value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-6 col-12 position-relative">
                        <DatePicker
                            label="To date"
                            value={toDate}
                            minDate={fromDate ? _parseDdMmYyyy(fromDate) : undefined}
                            maxDate={new Date()}
                            dateFormat={"dd-MM-yyyy"}
                            displayFormat={"dd MMM yyyy"}
                            removeLabelOnFocus={true}
                            dob={(value) => setToDate(value)}
                        />
                    </div>
                    {/* <div className="col-lg-3 col-md-6 col-12">
                        <CustomSearchSelectBox
                            options={STATUS_OPTIONS}
                            value={selectedStatus}
                            id="status-dropdown"
                            hide_error_text={false}
                            placeholder="Status"
                            isSearchable={true}
                            onSelectChange={setSelectedStatus}
                        />
                    </div> */}
                    <div className="col-lg-3 col-md-6 col-12">
                        <CustomSearchSelectBox
                            options={DEPOSITORY_OPTIONS}
                            value={selectedDepository}
                            id="depository-dropdown"
                            hide_error_text={false}
                            placeholder="Depository"
                            isSearchable={true}
                            onSelectChange={setSelectedDepository}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-12 d-flex align-items-center gap-3">
                        <GradientButton
                            onPress={_handleApplyFilters}
                            loading={btnLoader}
                            label="Apply filter"
                            className="h-fit-content"
                        />
                        <OutlineButton
                            label="Clear filter"
                            onPress={_clearFilters}
                            className="h-fit-content"
                        />
                    </div>
                </div>

                <div className="row mt-5">
                    {records.length !== 0 &&
                        <div className="col-12 text-end mb-3 e-table-scroll-indicator d-sm-block d-none">
                            <p className="color-outer-space e-poppins-regular e-font-12 mb-0">
                                <img draggable={false} src={require("../../Assets/Images/scroll-gif.gif")} alt="scroll"
                                    width="24px"
                                    height="24px"
                                    className="e-rotate-gif" />
                                Scroll horizontally for view more information
                            </p>
                        </div>
                    }

                    {
                        apiLoader === true ?
                            <div className="ps-4 pe-lg-5 pe-md-2 pe-2 padding-42px-top">
                                <div className="row h-80vh">
                                    <CustomLoader />
                                </div>
                            </div>
                            :
                            records.length !== 0 ?
                                <div className="min-height-80vh">
                                    <div className="col-12 position-relative" id="e-table-body">
                                        <DataTableContainer
                                            columns={SUMMARY_COLUMNS}
                                            data={records}
                                            customStyles={TABLE_STYLE}
                                            pagination={true}
                                            paginationServer={true}
                                            paginationTotalRows={totalLength}
                                            paginationPerPage={pageSize}
                                            paginationComponentOptions={paginationData}
                                            onChangeRowsPerPage={_handleRowsPerPageChange}
                                        />
                                        <div className="d-flex justify-content-end align-items-center mt-3 position-absolute bottom-16px right-24 w-max-content">
                                            <Icon icon="pagination-previous"
                                                width="24px"
                                                height="24px"
                                                className={pageNumber === 1 ? "pointer-events-none opacity-4 mx-3" : "mx-3 cursor-pointer"}
                                                onClick={_previousPage}
                                            />
                                            <Icon icon="pagination-next"
                                                width="24px"
                                                height="24px"
                                                className={pageNumber >= totalPages ? "ms-1 pointer-events-none opacity-4" : "ms-1 cursor-pointer"}
                                                onClick={_nextPage}
                                            />
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="mb-5">
                                    <EmptyScreen />
                                </div>
                    }
                </div>
            </div>

            {
                uploadModal === true && (
                    <div className="modal fade"
                        id="obligation-upload-modal"
                        tabIndex="-1"
                        aria-labelledby="obligation-upload-modal"
                        aria-hidden="true"
                        data-bs-backdrop="true">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content bg-white padding-32px-all border-radius-16px">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h3 className="color-black e-poppins-semi-bold e-font-18 mb-0">
                                            Upload obligation
                                        </h3>
                                        <p className="color-outer-space e-poppins-regular e-font-12 mb-0 mt-2">
                                            Supports only .xlsx or .xlsm files
                                        </p>
                                    </div>
                                    <Icon icon="close"
                                        size={24}
                                        className="cursor-pointer ms-auto e-modal-close-btn"
                                        data-bs-dismiss="modal" />
                                </div>

                                <div className="mt-4">
                                    <FileUpload
                                        id="obligation-upload-file"
                                        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                                        type={1}
                                        error=""
                                        loading={false}
                                        title="Select obligation file"
                                        content="Supports only .xlsx or .xlsm"
                                        onSelectFile={(path, file) => _handleFileChange(path, file)}
                                    />
                                </div>

                                {
                                    selectedFileName &&
                                    <div className="mt-3">
                                        <FilePreview
                                            fileName={selectedFileName}
                                            fileSize={selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : null}
                                            removeFile={() => {
                                                setSelectedFile(null);
                                                setSelectedFileName("");
                                            }}
                                        />
                                    </div>
                                }

                                <div className="mt-4">
                                    <GradientButton
                                        label={uploadLoader ? "Uploading..." : "Upload file"}
                                        loading={uploadLoader}
                                        disabled={!selectedFile || uploadLoader}
                                        className="w-100"
                                        type={1}
                                        onPress={_uploadObligationFile}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default ObligationReport;
